import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import { SseEvent, SseEventType, isSseEvent } from '@api-types/sse/sse.types';
import { KeycloakClient } from '@core/services/keycloak/keycloak.client';
import { EventSource } from 'eventsource';
import { SSE_EVENT_HANDLER } from './sse-event-handler';
import { SSE_RECONNECT_HANDLER } from './sse-reconnect-handler';

type ConnectionStatus =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'error';

const SSE_CONFIG = {
  endpoint: '/api/v1/sse/events',
  maxReconnectAttempts: 5,
  initialReconnectDelayMs: 1000,
  maxReconnectDelayMs: 30000,
} as const;

const sseLogger = {
  info: console.info.bind(console, '[SSE]'),
  warn: console.warn.bind(console, '[SSE]'),
  error: console.error.bind(console, '[SSE]'),
};

// manages sse connection to backend and dispatches events to registered handlers
@Injectable({ providedIn: 'root' })
export class ServerEventService implements OnDestroy {
  private readonly zone = inject(NgZone);

  private readonly keycloakClient = inject(KeycloakClient);

  private readonly handlers =
    inject(SSE_EVENT_HANDLER, { optional: true }) ?? [];

  private readonly reconnectHandlers =
    inject(SSE_RECONNECT_HANDLER, { optional: true }) ?? [];

  private eventSource: EventSource | null = null;
  private status: ConnectionStatus = 'disconnected';
  private reconnectAttempts = 0;
  private reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private visibilityChangeHandler: (() => void) | null = null;
  private hasConnectedBefore = false;

  constructor() {
    this.setupVisibilityHandler();

    if (this.handlers.length > 0) {
      sseLogger.info(`Registered ${this.handlers.length} handler(s)`);
    }
  }

  ngOnDestroy(): void {
    this.disconnect();
    this.removeVisibilityHandler();
  }

  connect(): void {
    if (this.eventSource) {
      return;
    }

    const token = this.keycloakClient.getAccessToken();
    if (!token) {
      sseLogger.warn('No auth token, skipping connection');
      this.status = 'disconnected';
      return;
    }

    this.status = 'connecting';
    this.createConnection(token);
  }

  disconnect(): void {
    this.cancelReconnect();

    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }

    this.status = 'disconnected';
  }

  private notifyReconnect(): void {
    for (const handler of this.reconnectHandlers) {
      try {
        handler.onReconnect();
      } catch (error) {
        sseLogger.error('Reconnect handler error:', error);
      }
    }
  }

  private dispatchToHandlers(event: SseEvent): void {
    for (const handler of this.handlers) {
      if (handler.eventType !== event.type) {
        continue;
      }

      try {
        handler.handle(event.payload);
      } catch (error) {
        sseLogger.error(`Handler error for ${event.type}:`, error);
      }
    }
  }

  private createConnection(token: string): void {
    try {
      this.eventSource = new EventSource(SSE_CONFIG.endpoint, {
        fetch: (input, init) =>
          fetch(input, {
            ...init,
            headers: {
              ...init?.headers,
              Authorization: `Bearer ${token}`,
            },
          }),
      });

      this.eventSource.onopen = () => {
        this.zone.run(() => {
          sseLogger.info('Connected');
          this.status = 'connected';
          this.reconnectAttempts = 0;

          if (this.hasConnectedBefore) {
            this.notifyReconnect();
          }
          this.hasConnectedBefore = true;
        });
      };

      this.eventSource.onmessage = (event: MessageEvent) => {
        this.zone.run(() => {
          try {
            const sseEvent = JSON.parse(event.data);

            if (!isSseEvent(sseEvent)) {
              sseLogger.warn('Invalid event format:', sseEvent);
              return;
            }

            // heartbeats are just for keepalive, don't dispatch
            if (sseEvent.type !== SseEventType.Heartbeat) {
              this.dispatchToHandlers(sseEvent);
            }
          } catch (error) {
            sseLogger.error('Failed to parse event:', error);
          }
        });
      };

      this.eventSource.onerror = () => {
        this.zone.run(() => {
          sseLogger.error('Connection error');
          this.status = 'error';
          this.handleReconnect();
        });
      };
    } catch (error) {
      sseLogger.error('Failed to create connection:', error);
      this.status = 'error';
      this.handleReconnect();
    }
  }

  private handleReconnect(): void {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }

    if (this.reconnectAttempts >= SSE_CONFIG.maxReconnectAttempts) {
      sseLogger.error('Max reconnect attempts reached');
      this.status = 'disconnected';
      return;
    }

    const delay = Math.min(
      SSE_CONFIG.initialReconnectDelayMs * Math.pow(2, this.reconnectAttempts),
      SSE_CONFIG.maxReconnectDelayMs,
    );

    this.reconnectAttempts++;
    this.status = 'reconnecting';

    sseLogger.info(
      `Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${SSE_CONFIG.maxReconnectAttempts})`,
    );

    this.reconnectTimeoutId = setTimeout(() => {
      const token = this.keycloakClient.getAccessToken();
      if (token) {
        this.createConnection(token);
      } else {
        sseLogger.warn('No auth token for reconnect');
        this.status = 'disconnected';
      }
    }, delay);
  }

  private cancelReconnect(): void {
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }
  }

  // reconnects when tab becomes visible after being disconnected
  private setupVisibilityHandler(): void {
    this.visibilityChangeHandler = () => {
      if (document.visibilityState === 'visible') {
        if (this.status === 'disconnected' || this.status === 'error') {
          sseLogger.info('Tab visible, reconnecting');
          this.reconnectAttempts = 0;
          this.connect();
        }
      }
    };

    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
  }

  private removeVisibilityHandler(): void {
    if (this.visibilityChangeHandler) {
      document.removeEventListener(
        'visibilitychange',
        this.visibilityChangeHandler,
      );
      this.visibilityChangeHandler = null;
    }
  }
}
